import { useTranslator } from '@bluebeam/react-i18n';
import { SelectOption } from 'components/inputs/BaseSelect';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { FC, useContext, useState, useEffect } from 'react';
import { Project } from 'records/Project';
import { PunchStatus, PunchStatusValue } from 'records/Punch';

interface Props {
  id: string;
  onChange: (value: PunchStatusValue) => void;
  value: PunchStatusValue | string;
  placeholder?: string;
  disabled?: boolean;
}

interface Option {
  label: string;
  value: PunchStatusValue | string;
  permission: string;
}

interface Options {
  [key: string]: Option;
}

const StatusSelect: FC<Props> = ({
  id,
  onChange,
  value,
  placeholder,
  disabled = false,
}: Props) => {
  const context = useContext(AppStoreContext);
  const [options, setOptions] = useState<Options>({});
  const [selectedValue, setSelectedValue] = useState<string>('OPEN');
  const { projects, selectedProject } = context.state;
  const project = projects.get(selectedProject, new Project());
  const { statuses, permissions } = project;
  const allOptions: Options = {};
  const { translate } = useTranslator(['statusLabels']);

  const setAllOptions = () => {
    allOptions[PunchStatusValue.OPEN] = {
      value: PunchStatusValue.OPEN,
      label: translate('statusLabels:statuses.OPEN'),
      permission: 'setStatusOpen',
    };
    allOptions[PunchStatusValue.CLOSED] = {
      value: PunchStatusValue.CLOSED,
      label: translate('statusLabels:statuses.CLOSED'),
      permission: 'setStatusClosed',
    };
    allOptions[PunchStatusValue.READY] = {
      value: PunchStatusValue.READY,
      label: translate('statusLabels:statuses.READY_FOR_REVIEW'),
      permission: 'setStatusReview',
    };
    allOptions[PunchStatusValue.OUT_OF_SCOPE] = {
      value: PunchStatusValue.OUT_OF_SCOPE,
      label: translate('statusLabels:statuses.OUT_OF_SCOPE'),
      permission: 'setStatusOutOfScope',
    };
    allOptions.multiple = {
      value: PunchStatusValue.OUT_OF_SCOPE,
      label: translate('statusLabels:statuses.OUT_OF_SCOPE'),
      permission: 'setStatusOpen',
    };
  };

  useEffect(() => {
    setAllOptions();
    const o = statuses.reduce((acc: Options, status: PunchStatus) => {
      const option = allOptions[status.status];
      if (
        permissions.has(option.permission) &&
        permissions.get(option.permission)
      ) {
        acc[status.status] = option;
      }

      return acc;
    }, {});

    setOptions(o);
  }, []);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleStatusChange = (option: SelectOption): void => {
    const value = option.value as PunchStatusValue;
    onChange(value);
  };

  return (
    <SingleSelect
      id={id}
      disabled={disabled}
      value={options[selectedValue]}
      options={Object.values(options)}
      onChange={(option: SelectOption) => handleStatusChange(option)}
      placeholder={placeholder}
    />
  );
};

export default StatusSelect;
